import React, { useEffect, useRef } from 'react';

import { useIntl } from '../../../../util/reactIntl';
import { formatDateContext, parseDateContext } from '../../../../util/searchDates';
import { regionSummary } from '../TopbarSearchForm/SearchRegionSegment';
import SearchIcon from '../SearchIcon';

import css from './TopbarMobileSearchBar.module.css';

/**
 * The search bar on phones: the desktop pill's questions and answers as one tappable bar that
 * opens the mobile search sheet. Below --viewportLarge a magnifier icon was the only way in, and
 * visitors did not find it.
 *
 * Nothing here is editable - the sheet asks the questions. The bar only reports the answers,
 * worded exactly as the pill words them.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.dates current `dates` query param
 * @param {string?} props.dateMode current `dateMode` query param
 * @param {Array<string>?} props.regions selected canton option keys
 * @param {boolean} props.showDates whether the pill asks Wann at all
 * @param {Function} props.onOpen opens the mobile search sheet
 * @param {Function?} props.onInViewChange called with whether the bar is on screen
 * @returns {JSX.Element} phone search bar
 */
const TopbarMobileSearchBar = props => {
  const { dates, dateMode, regions, showDates, onOpen, onInViewChange } = props;
  const intl = useIntl();
  const rootRef = useRef(null);

  // Off screen means folded away with the rail or scrolled under the sticky row, which the top
  // margin takes out of the viewport. By ratio rather than isIntersecting: a box the rail has
  // clipped to nothing still touches the sticky row's edge, and touching counts as intersecting.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onInViewChange || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const stickyRow = getComputedStyle(document.documentElement).getPropertyValue('--topbarHeight');
    const observer = new IntersectionObserver(
      ([entry]) => onInViewChange(entry.intersectionRatio >= 0.5),
      { rootMargin: `-${parseInt(stickyRow, 10) || 0}px 0px 0px 0px`, threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onInViewChange]);

  const dateLabel = showDates ? formatDateContext(parseDateContext(dates, dateMode), intl) : null;
  const regionLabel = regionSummary(regions, intl);

  const segment = (label, value, placeholder) => (
    <span className={css.segment}>
      <span className={css.label}>{label}</span>
      <span className={value ? css.valueSet : css.value}>{value || placeholder}</span>
    </span>
  );

  return (
    <div ref={rootRef} className={css.root}>
      <button type="button" className={css.bar} aria-haspopup="dialog" onClick={onOpen}>
        {showDates
          ? segment(
              intl.formatMessage({ id: 'TopbarSearchForm.dateLabel' }),
              dateLabel,
              intl.formatMessage({ id: 'TopbarSearchForm.datePlaceholder' })
            )
          : null}
        {segment(
          intl.formatMessage({ id: 'TopbarSearchForm.regionLabel' }),
          regionLabel,
          intl.formatMessage({ id: 'TopbarSearchForm.regionPlaceholder' })
        )}
        <span className={css.submit} aria-hidden="true">
          <SearchIcon rootClassName={css.submitIcon} />
        </span>
      </button>
    </div>
  );
};

export default TopbarMobileSearchBar;
